import { catchError, first, map, switchMap, take, tap } from 'rxjs/operators';
import { select, Store } from '@ngrx/store';
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '../../../environments/environment';
import { Survey } from '../_models/survey';
import { User } from '../_models/user';
import { Equipment } from '../_models';
import { AppState } from '../_reducers';
import { selectAllUsers } from '../_selectors/user.selectors';
import { selectCurrentUser } from '../_selectors/currentUser.selectors';
import * as UserActions from '../../common/_actions/user';

@Injectable()
export class UserService {
  private apiBaseUrl = environment.apiBaseUrl;
  private userList$: Observable<User[]>;
  private userMe$ = new BehaviorSubject<User>(null);

  isSupplyChainDestinationFilterActive: boolean;
  showSupplyChainDestinationFilter: boolean;

  currentUser: User = null;

  constructor(
    private http: HttpClient,
    private store: Store<AppState>,
    private translate: TranslateService
  ) {
    this.userList$ = this.store.pipe(select(selectAllUsers));
    this.store.select(selectCurrentUser).subscribe((user) => {
      this.userMe$.next(user);
      this.currentUser = user;
    });
  }

  getFromLocalStore(): Observable<User[]> {
    return this.userList$;
  }

  getUserMeFromLocalStore(): Observable<User> {
    return this.userMe$.asObservable();
  }

  getAllMultiFarmUsers(): Observable<User[]> {
    return this.http
      .get<User[]>(this.apiBaseUrl + 'api/private/v2/auth/users?all=true')
      .pipe(map((response) => response));
  }

  /**
   * Fetches all users from the API and
   * updates the lastName of users who are disabled or locked (aka Removed)
   * by appending a removal note and the last updated date (formatted as DD-MM-YYYY in UTC).
   *
   * @returns Observable<User[]> - An observable emitting the processed list of users
   */
  getUsers(): Observable<User[]> {
    return this.http.get<User[]>(this.apiBaseUrl + 'api/private/v2/auth/users').pipe(
      map((response: User[]) => {
        const usersList = response.map((user) => {
          // Update the lastName of a Removed user
          if (user.enabled === false || user.accountLocked === true) {
            return this.renameRemovedUser(user);
          }
          return user;
        });
        return usersList;
      })
    );
  }

  /**
   * Fetches the current authenticated user from the API.
   *
   * @returns Observable<User> - An observable emitting the current user
   */
  getUserMe(): Observable<User> {
    return this.http
      .get<User>(this.apiBaseUrl + 'api/private/v2/auth/users/me')
      .pipe(map((response) => response));
  }

  /**
   * Returns an observable emitting the user with the specified id from the local store.
   * Emits null if not found.
   *
   * @param id - The user id to search for
   * @returns Observable<User>
   */
  getUserById(id: number): Observable<User> {
    return this.userList$.pipe(
      first((list) => list && list.length > 0),
      map((list: User[]) => list.find((user) => user.id === id) || null)
    );
  }

  /**
   * Synchronously returns the user with the specified id from the local store.
   * Returns null if not found.
   *
   * @param id - The user id to search for
   * @returns User | null
   */
  getUserByIdSync(id: number): User | null {
    let usersArray: User[] = [];
    this.userList$
      .subscribe((users) => {
        usersArray = users;
      })
      .unsubscribe();
    return usersArray.find((u) => u.id === id) || null;
  }

  /**
   * Saves a user by either updating or creating, depending on whether the user exist or not.
   * Returns null if the current user is not available.
   *
   * @param user - The user object to save (partial)
   * @returns Observable<User | null> - An observable emitting the saved user or null
   */
  saveUser(user: Partial<User>): Observable<User | null> {
    return this.getUserMeFromLocalStore().pipe(
      take(1),
      switchMap((userStore) => {
        if (!userStore?.id) {
          return of(null);
        }

        const userPayload = this.prepareUserPayload(user);

        return userPayload.id
          ? this.updateUser(userPayload, user)
          : this.createUser(userPayload, userStore);
      })
    );
  }

  /**
   * Deletes a user by id via the API.
   *
   * @param id - The id of the user to delete
   * @returns Observable<User> - An observable emitting an object with the deleted user's id
   */
  deleteUser(id: number): Observable<User> {
    return this.http.delete(this.apiBaseUrl + 'api/private/v2/auth/users/' + id).pipe(
      map(() => {
        return { id } as User;
      })
    );
  }

  saveSurvey(survey): Observable<Survey> {
    delete survey.lastUpdated;
    delete survey.dateCreated;

    const companySelected = JSON.parse(localStorage.getItem('companySelected'));
    survey.companyId = companySelected ? companySelected.id : null;

    return this.http
      .post<Survey>(this.apiBaseUrl + 'api/private/v2/surveys/', survey)
      .pipe(map((response) => response));
  }

  // Todo: move to a dedicated service
  sendToEquipment(equipment: Equipment) {
    const companyId = equipment.companyId ? equipment.companyId : null;

    const resource = equipment.externalResources.find(
      (r) => r.externalSource === 'FADE' || r.externalSource === 'CLAAS'
    );
    const { externalSource } = resource;
    const { externalId } = resource;

    const url = `${this.apiBaseUrl}api/private/v3/isoxml/users/${companyId}/send?externalSource=${externalSource}&equipmentId=${externalId}`;
    return this.http.post(url, {});
  }

  // Todo: move to a dedicated service
  getUserSettings(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiBaseUrl}api/private/v3/analytics/user-settings`).pipe(
      map((response) => this.setCommoditySettingList(response)),
      catchError((error) => {
        if (error.status === 404) {
          return this.saveUserSettingsCommodities([]).pipe(switchMap(() => this.getUserSettings()));
        }
        return of([]);
      })
    );
  }

  // Todo: move to a dedicated service
  saveUserSettingsCommodities(commodityList: any[]): Observable<any[]> {
    const obj = { lastSelectedCommodities: [...new Set(commodityList)] };
    return this.http
      .post<any[]>(`${this.apiBaseUrl}api/private/v3/analytics/user-settings`, obj)
      .pipe(map((response) => response));
  }

  // Todo: move to a dedicated service
  saveUserSettingsSupplyChainDestinationFilterActive(isOnly: boolean): Observable<any[]> {
    this.isSupplyChainDestinationFilterActive = isOnly;
    const obj = { isSupplyChainDestinationFilterActive: this.isSupplyChainDestinationFilterActive };
    return this.http
      .post<any[]>(`${this.apiBaseUrl}api/private/v3/analytics/user-settings`, obj)
      .pipe(map((response) => response));
  }

  // Todo: move to a dedicated service
  sendQdcaRequest(preferredEmail: string) {
    const url = `${this.apiBaseUrl}api/private/v1/store/qdca`;
    const params = new HttpParams().set('preferredEmail', preferredEmail);

    return this.http.post(url, {}, { params });
  }

  // Todo: move to a dedicated service
  createLeadRequest(phone: number, email?: string) {
    const url = `${this.apiBaseUrl}api/private/v1/store/lead/qdca`;
    const params = new HttpParams({
      fromObject: {
        phone: phone.toString(),
        ...(email ? { preferredEmail: email } : {}),
      },
    });
    return this.http.post(url, {}, { params });
  }

  // -- HELPERS -- //
  /**
   * Appends a removal note and last updated date to the user's lastName if the user is removed (disabled or locked).
   * The date is formatted as DD-MM-YYYY in UTC.
   *
   * @param user - The user object to update
   * @returns A new user object with the updated lastName
   */
  private renameRemovedUser(user: User): User {
    const lastUpdated = user.lastUpdated ? new Date(user.lastUpdated) : null;
    let formattedDate = '';
    if (lastUpdated && !isNaN(lastUpdated.getTime())) {
      // Format as DD-MM-YYYY in UTC
      const day = String(lastUpdated.getUTCDate()).padStart(2, '0');
      const month = String(lastUpdated.getUTCMonth() + 1).padStart(2, '0');
      const year = lastUpdated.getUTCFullYear();
      formattedDate = `${day}-${month}-${year}`;
    }
    return {
      ...user,
      lastName:
        user.lastName +
        ' - (' +
        this.translate.instant('users_select.remove_on', {}) +
        (formattedDate ? ' ' + formattedDate : '') +
        ')',
    };
  }

  private prepareUserPayload(user: Partial<User>): any {
    const { farm, ...payload } = user;

    delete payload.lastUpdated;
    delete payload.dateCreated;
    delete payload.userMultiFarmToken;

    return {
      ...payload,
      farm: farm ? { id: farm.id } : undefined,
    };
  }

  private updateUser(payload: any, originalUser: Partial<User>): Observable<User> {
    return this.http
      .put<User>(`${this.apiBaseUrl}api/private/v2/auth/users/${payload.id}`, payload)
      .pipe(
        tap((responseUser) => {
          if (originalUser?.id === this.currentUser?.id && !originalUser.userMultiFarmToken) {
            responseUser.userMultiFarmToken = originalUser.userMultiFarmToken;

            this.store.dispatch(UserActions.updateUserSuccess({ user: responseUser }));
          }
        })
      );
  }

  private createUser(payload: any, userStore: any): Observable<User> {
    if (userStore.id !== payload.id) {
      const companySelected = JSON.parse(localStorage.getItem('companySelected'));
      payload.farm = companySelected ? companySelected.id : null;
    }

    return this.http.post<User>(`${this.apiBaseUrl}api/private/v2/auth/users/`, payload);
  }

  private setCommoditySettingList(settings: any) {
    this.isSupplyChainDestinationFilterActive = settings.isSupplyChainDestinationFilterActive;
    this.showSupplyChainDestinationFilter = settings.showSupplyChainDestinationFilter;
    return settings;
  }
}
